/**
 * useAutoSwap — preflight cleanup hook for local-multisite-auto-swap.
 *
 * On local environments only, when the user starts a deploy and another
 * site is already running, this hook walks the user through stopping
 * the other stack first (so docker container_name + host port collisions
 * don't kill the new deploy). It does NOT call startBatch() — that
 * responsibility stays in useDeployActions.confirmDeploy. Keeping the
 * hook focused on preflight cleanup makes it usable from any future
 * trigger (e.g. site row's "switch to" action) without dragging the
 * full deploy lifecycle along.
 *
 * Flow:
 *   1. !isLocalEnvironment()        → swapped:false, reason:not-local
 *   2. no other site running        → swapped:false, reason:no-other-site
 *   3. confirmFn() returns false    → swapped:false, reason:cancelled
 *   4. stopSite() throws            → swapped:false, reason:error
 *   5. waitForStopCompletion fails  → swapped:false, reason:error
 *   6. ok                           → swapped:true, stoppedSite
 */
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import siteBatchService from '../services/SiteBatchService'
import { Project } from '../models/Project'
import { RunningSite } from '../models/SiteBatch'
import { isLocalEnvironment } from '../utils/environment'
import { runningSitesQueryKey } from './useRunningSites'

export type AutoSwapResult =
  | { swapped: false; reason: 'not-local' }
  | { swapped: false; reason: 'no-other-site' }
  | { swapped: false; reason: 'cancelled' }
  | { swapped: false; reason: 'error'; message: string }
  | { swapped: true; stoppedSite: string }

interface UseAutoSwapOptions {
  /** Map of site name → Project, for projectId lookup when stopping. */
  sitesByName: Map<string, Project>
}

interface CheckAndSwapParams {
  target: Project
  runningSites: RunningSite[]
  /** Resolves true if user accepts the swap, false if rejected. */
  confirmFn: (otherSite: string, targetSite: string) => Promise<boolean>
}

function describeError(err: unknown, otherSite: string): string {
  if (err instanceof Error) {
    if (err.message === 'stop_timeout') {
      return `${otherSite} 중단이 60초 내 완료되지 않았습니다. 수동으로 중단 후 재시도하세요.`
    }
    return `${otherSite} 중단 중 오류: ${err.message}`
  }
  return `${otherSite} 중단 중 알 수 없는 오류가 발생했습니다.`
}

export function useAutoSwap(options: UseAutoSwapOptions) {
  const { sitesByName } = options
  const queryClient = useQueryClient()
  const [isSwapping, setIsSwapping] = useState(false)

  const checkAndSwap = useCallback(
    async (params: CheckAndSwapParams): Promise<AutoSwapResult> => {
      const { target, runningSites, confirmFn } = params

      if (!isLocalEnvironment()) {
        return { swapped: false, reason: 'not-local' }
      }

      const others = runningSites.filter((rs) => rs.site !== target.name)
      if (others.length === 0) {
        return { swapped: false, reason: 'no-other-site' }
      }

      // 시나리오상 동시 다중 running은 드물지만 안전하게 첫 번째만 처리.
      // 추가 사이트가 남아있으면 사용자의 다음 배포 시도에서 다시 감지된다.
      const other = others[0]

      const accepted = await confirmFn(other.site, target.name)
      if (!accepted) {
        return { swapped: false, reason: 'cancelled' }
      }

      const otherProject = sitesByName.get(other.site)
      if (!otherProject) {
        return {
          swapped: false,
          reason: 'error',
          message: `사이트 ${other.site}의 project id를 찾을 수 없습니다.`,
        }
      }

      setIsSwapping(true)
      try {
        await siteBatchService.stopSite(otherProject.id, other.site)
        await siteBatchService.waitForStopCompletion(otherProject.id, other.site)

        // running-sites는 batch 종료 시점에서야 갱신되므로 즉시 invalidate.
        // active-batches / active-deployment 갱신은 useDeployActions 가
        // startBatch 직후에 처리(invalidateBatchQueries) — 여기서는 중복 회피.
        await queryClient.invalidateQueries({
          queryKey: runningSitesQueryKey(target.id),
        })

        return { swapped: true, stoppedSite: other.site }
      } catch (err) {
        return {
          swapped: false,
          reason: 'error',
          message: describeError(err, other.site),
        }
      } finally {
        setIsSwapping(false)
      }
    },
    [sitesByName, queryClient],
  )

  return { checkAndSwap, isSwapping }
}
